import clsx from 'clsx';
import Tabs from 'components/Table/Tabs';
import { useQueryState } from 'hooks/useQueryState';
import {
  HTMLAttributes,
  ReactNode,
  useRef,
  useState,
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
} from 'react';
import { LocationOutline, Menu } from 'react-ionicons';
import OrdersTable, { OrderType, ViewByType } from './OrdersTable';
import { usePopper } from 'react-popper';

interface OrdersProps {
  graphStatus: string | null;
  setGraphStatus: Dispatch<SetStateAction<string | null>>;
}

const Orders: FC<OrdersProps> = ({ graphStatus, setGraphStatus }) => {
  const [activeTab, setActiveTab] = useQueryState('order', {
    defaultValue: 'all' as OrderType,
    getParam: (value: string) => value as OrderType,
    setParam: (value: string) => value,
  });

  const [viewByTab, setOrderTab] = useQueryState('viewBy', {
    defaultValue: 'location' as ViewByType,
    getParam: (value: string) => value as ViewByType,
    setParam: (value: string) => value,
  });

  useEffect(() => {
    if (graphStatus) {
      setActiveTab('all');
      setOrderTab('order');
    }
  }, [graphStatus, setActiveTab, setOrderTab]);

  const _activeTab = useMemo(() => {
    if (activeTab === 'in_progress') return 1;
    if (activeTab === 'completed') return 2;
    else return 0;
  }, [activeTab]);

  const onTabClick = (tab: OrderType) => {
    setActiveTab(tab);
    setGraphStatus(null);
  };

  const onViewByClick = (tab: ViewByType) => {
    setOrderTab(tab);
    setGraphStatus(null);
  };

  const tabs = [
    {
      name: 'all',
      title: 'All orders',
      onClick: () => onTabClick('all'),
    },
    {
      name: 'progress',
      title: 'In progress',
      onClick: () => onTabClick('in_progress'),
    },
    {
      name: 'completed',
      title: 'Completed',
      onClick: () => onTabClick('completed'),
    },
  ];

  return (
    <div className="flex h-full w-full flex-col rounded bg-background-base-surface-2 p-6 shadow-elevation3">
      <div className="relative mb-8 flex">
        <Tabs tabs={tabs} defaultActiveTab={_activeTab} />
        <div className="absolute -top-1 right-0 flex gap-2">
          <Tooltip
            message="View by location"
            className={clsx('cursor-pointer rounded p-1 ', {
              'bg-button-background-primary-default text-content-flip-default':
                viewByTab === 'location',
              'text-content-base-default': viewByTab !== 'location',
            })}
            offset={[58, 11]}
            onClick={() => onViewByClick('location')}
          >
            <LocationOutline width="24px" height="24px" color="currentColor" />
          </Tooltip>
          <Tooltip
            message="View by service"
            className={clsx('cursor-pointer rounded p-1', {
              'bg-button-background-primary-default text-content-flip-default':
                viewByTab === 'order',
              'text-content-base-default': viewByTab !== 'order',
            })}
            onClick={() => onViewByClick('order')}
          >
            <Menu width="24px" height="24px" color="currentColor" />
          </Tooltip>
        </div>
      </div>
      <OrdersTable
        activeTab={activeTab}
        viewByTab={viewByTab}
        graphStatus={graphStatus}
      />
    </div>
  );
};

export default Orders;

interface TooltipProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  message: string;
  offset?: [number, number];
}

const Tooltip: FC<TooltipProps> = ({
  children,
  message,
  offset = [56, 11],
  ...props
}) => {
  const [visible, setVisible] = useState(false);
  const referenceRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);

  const { styles, attributes, forceUpdate } = usePopper(
    referenceRef.current,
    popperRef.current,
    {
      placement: 'bottom',
      strategy: 'fixed',
      modifiers: [{ name: 'offset', options: { offset } }],
    },
  );
  return (
    <div
      {...props}
      ref={referenceRef}
      onMouseEnter={() => {
        setVisible(true);
        forceUpdate?.();
      }}
      onMouseLeave={() => {
        setVisible(false);
      }}
    >
      <div
        ref={popperRef}
        style={styles.popper}
        {...attributes.popper}
        className={`pointer-events-none z-[100] w-max transition-opacity duration-150 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="relative rounded-lg bg-background-base-surface-0 p-5 text-sm text-content-base-subdued shadow-elevation3">
          {message}
          <div className="absolute -top-1 left-[5%] scale-x-[0.84] transform">
            <div className="h-[24px] w-[24px] rotate-45 bg-background-base-surface-0" />
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};
